import { formatDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import LogoMark from "@/components/LogoMark";
import { downloadImage, printElement } from "@/lib/pdf";
import { Printer, Image as ImageIcon } from "lucide-react";

const DAYS = ["Minggu", "Senin", "Selasa", "Rabu", "Kamis", "Jumat", "Sabtu"];
const dayName = (iso) => {
  if (!iso) return "________";
  const d = new Date(iso.slice(0, 10));
  return isNaN(d.getTime()) ? "________" : DAYS[d.getDay()];
};
const minusOneDay = (iso) => {
  if (!iso) return "";
  const d = new Date(iso.slice(0, 10));
  if (isNaN(d.getTime())) return iso;
  d.setDate(d.getDate() - 1);
  return d.toISOString().slice(0, 10);
};

export default function KitchenNote({ order, open, onClose }) {
  if (!order) return null;
  const o = order;
  const putra = (o.child_gender || o.gender) === "perempuan" ? "Putri" : "Putra";
  const sex = (o.lamb_sex || "").toLowerCase() === "betina" ? "BETINA" : "JANTAN";
  const eventDate = o.event_date || o.schedule_date;
  const slaughterDate = minusOneDay(eventDate);
  const recvTime = o.delivery_time || o.event_time || "____";

  const rows = [];
  if (o.lamb_type || o.unit_price) rows.push({ label: `Domba : ${(o.lamb_type || "-").toUpperCase()} - ${sex} (${(o.lamb_condition || "dimasak") === "mentah" ? "MENTAH" : "DIMASAK"})`, qty: o.lamb_count || 1 });
  if (o.rice_box_type || o.box_count) rows.push({ label: `Nasi Box : ${(o.rice_box_type || "-").toUpperCase()}`, qty: o.box_count || 0 });
  (o.addons || []).forEach((a) => rows.push({ label: `Add On : ${(a.name || "-").toUpperCase()}`, qty: a.qty || 1 }));

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto p-0">
        <DialogTitle className="sr-only">Form Pesanan ke Dapur {o.order_number}</DialogTitle>
        <div className="flex items-center justify-between border-b border-brand-line px-6 py-4 no-print">
          <h2 className="font-serif text-xl font-bold text-brand-espresso">Form Pesanan ke Dapur</h2>
          <div className="flex gap-2">
            <Button onClick={() => downloadImage("kitchen-print", `form-dapur-${o.order_number}.jpg`)} data-testid="jpg-kitchen-btn"
              variant="outline" className="gap-2 border-brand-line">
              <ImageIcon className="h-4 w-4" /> JPG
            </Button>
            <Button onClick={() => printElement("kitchen-print", { landscape: true })} data-testid="print-kitchen-btn"
              className="gap-2 bg-brand-gold text-brand-espresso hover:bg-brand-goldhover">
              <Printer className="h-4 w-4" /> Cetak
            </Button>
          </div>
        </div>

        <div id="kitchen-print" className="bg-white p-6 text-brand-espresso">
          {/* Header */}
          <div className="flex items-center justify-between border-b-2 border-brand-gold pb-4">
            <div className="flex items-center gap-3">
              <LogoMark size={52} textClass="text-3xl" />
              <div className="leading-tight">
                <p className="font-serif text-2xl font-bold">Rizqia Aqiqah</p>
                <p className="text-[11px] uppercase tracking-[0.2em] text-brand-espresso">Hemat, Praktis, Amanah</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-xs uppercase tracking-wide text-brand-espresso">No. Pesanan</p>
              <p className="font-semibold">{o.order_number}</p>
            </div>
          </div>

          {/* Title */}
          <h1 className="mt-6 text-center font-serif text-3xl font-bold uppercase tracking-wide">Form Permohonan Produksi Aqiqah</h1>

          {/* Body */}
          <div className="mt-6 space-y-3 text-lg leading-relaxed">
            <p>
              Berikut disampaikan Pesanan Aqiqah atas nama : <span className="font-bold">{(o.child_name || o.aqiqah_name || "-")}</span>,{" "}
              {putra} dari (Ayah): <span className="font-medium">{o.father_name || "-"}</span>, dan (Ibu):{" "}
              <span className="font-medium">{o.mother_name || "-"}</span>.{" "}
              Pesanan ini disembelih pada Hari <span className="font-bold">{dayName(slaughterDate)}</span> tanggal{" "}
              <span className="font-bold">{formatDate(slaughterDate)}</span>.{" "}
              Pesanan tersebut diharapkan dapat diterima konsumen pada Hari <span className="font-bold">{dayName(eventDate)}</span>{" "}
              Tanggal <span className="font-bold">{formatDate(eventDate)}</span> Jam <span className="font-bold">{recvTime} WIB</span>,
              dan mohon agar pesanan tersebut dapat disiapkan 2 jam sebelum diterima.{" "}
              Perincian Pesanan atas nama konsumen tersebut adalah :
            </p>
          </div>

          {/* Table */}
          <table className="mt-3 w-full border border-gray-500 text-lg">
            <thead>
              <tr className="bg-gray-200 text-left">
                <th className="border border-gray-500 px-3 py-2 text-center w-12">No</th>
                <th className="border border-gray-500 px-3 py-2">Jenis Pesanan</th>
                <th className="border border-gray-500 px-3 py-2 text-center w-24">Qty</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i}>
                  <td className="border border-gray-500 px-3 py-2.5 text-center">{i + 1}</td>
                  <td className="border border-gray-500 px-3 py-2.5 font-medium">{r.label}</td>
                  <td className="border border-gray-500 px-3 py-2.5 text-center font-bold">{r.qty}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Catatan */}
          <div className="mt-5 rounded-lg bg-gray-100 p-4 text-lg">
            <span className="font-semibold">Catatan: </span>
            {o.notes ? o.notes : "-"}
          </div>

          <div className="mt-5 text-lg">
            <span className="font-semibold">Alamat Pengiriman: </span>
            {o.delivery_address ? o.delivery_address : "-"}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
